"use client";

import { currentMarketingTrafficContext } from "@/lib/analytics-traffic";
import {
  dispatchMarketingEventForTraffic,
  type MarketingEventAudience,
} from "@/lib/marketing-analytics-policy";
import type { HomeHeroCopyVariant } from "@/lib/marketing-experiment";
import { siteConfig } from "@/lib/site-config";

type MarketingEventPropertyValue = string | number | boolean | null | undefined;
type MarketingEventProperties = Record<string, MarketingEventPropertyValue>;
type MarketingPostHogModule = typeof import("@/lib/posthog");

type MarketingExperimentSyncInput = {
  homeHeroVariant: HomeHeroCopyVariant | null;
};

type MarketingDistinctIdOptions = {
  waitForIdle?: boolean;
};

type MarketingEventCaptureOptions = {
  analyticsAudience?: MarketingEventAudience;
};

let postHogModulePromise: Promise<MarketingPostHogModule> | null = null;

function loadMarketingPostHog() {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }

  postHogModulePromise ??= import("@/lib/posthog");

  return postHogModulePromise;
}

function reportAnalyticsLoadError(error: unknown) {
  if (process.env.NODE_ENV !== "production") {
    console.warn("Marketing analytics failed to load.", error);
  }
}

function runWithMarketingPostHog(
  callback: (module: MarketingPostHogModule) => void,
) {
  void loadMarketingPostHog()
    .then((module) => {
      if (module) {
        callback(module);
      }
    })
    .catch(reportAnalyticsLoadError);
}

function runIdleWithMarketingPostHog(
  callback: (module: MarketingPostHogModule) => void,
) {
  if (typeof window === "undefined") {
    return;
  }

  const run = () => runWithMarketingPostHog(callback);

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 2400 });
    return;
  }

  window.setTimeout(run, 900);
}

function waitForBrowserIdle() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => resolve(), { timeout: 2400 });
      return;
    }

    window.setTimeout(resolve, 900);
  });
}

export function ensureMarketingPostHog() {
  runIdleWithMarketingPostHog((module) => {
    module.ensureMarketingPostHog();
  });
}

export function captureMarketingEvent(
  event: string,
  properties: MarketingEventProperties = {},
  { analyticsAudience = "all" }: MarketingEventCaptureOptions = {},
) {
  const trafficContext = currentMarketingTrafficContext(
    siteConfig.posthog.environment,
  );

  dispatchMarketingEventForTraffic({
    analyticsAudience,
    trafficContext,
    send() {
      runWithMarketingPostHog((module) => {
        module.captureMarketingEvent(event, properties);
      });
    },
  });
}

export function captureMarketingPageview() {
  runIdleWithMarketingPostHog((module) => {
    module.captureMarketingPageview();
  });
}

export function captureMarketingException(
  error: unknown,
  properties: MarketingEventProperties = {},
) {
  runWithMarketingPostHog((module) => {
    module.captureMarketingException(error, properties);
  });
}

export async function syncMarketingAnalyticsConsentMode() {
  try {
    const analyticsModule = await loadMarketingPostHog();

    return analyticsModule?.syncMarketingAnalyticsConsentMode() ?? false;
  } catch (error) {
    reportAnalyticsLoadError(error);
    return false;
  }
}

export function syncMarketingExperimentBootstrap(input: MarketingExperimentSyncInput) {
  if (!input.homeHeroVariant) {
    return;
  }

  runWithMarketingPostHog((module) => {
    module.syncMarketingExperimentBootstrap(input);
  });
}

export async function getMarketingDistinctId({
  waitForIdle = true,
}: MarketingDistinctIdOptions = {}) {
  try {
    if (waitForIdle) {
      await waitForBrowserIdle();
    }

    const analyticsModule = await loadMarketingPostHog();

    return analyticsModule?.getMarketingDistinctId() ?? null;
  } catch (error) {
    reportAnalyticsLoadError(error);
    return null;
  }
}
